<template>
  <div class="mapbox-map-container">
    <mapbox
      ref="mapboxMap"
      access-token="pk.eyJ1IjoibWF4aXNtIiwiYSI6ImNqbDg0bHg4dzBkNjczdm84ZjRqZnFpcWMifQ.lekJSHeqixAGad36DXbEWg"
      :map-options="{
        style: 'mapbox://styles/tomganchos/cl5klwnix002l14s01lf54ii8',
        zoom: 0,
        center: center ? center : undefined,
        preserveDrawingBuffer: true,
        trackResize: true,
      }"
      :nav-control="{ show: false, position: 'bottom-right' }"
      @map-load="mapLoaded"
    />

    <slot />
  </div>
</template>

<script>
import Vue from 'vue';
import Mapbox from 'mapbox-gl-vue';
// import geojsonMerge from "@mapbox/geojson-merge";
// import centroid from "@turf/centroid";
import TurfBbox from '@turf/bbox';
import TurfDistance from 'turf-distance';
import { point as TurfPoint } from '@turf/helpers';
import { PortalTarget } from 'portal-vue';

let globalMap;
let markers = {};
let popup;
let clickedMarker;


const MAP_FOCUS_SPEED = 100;
const MAP_FOCUS_ZOOM = 12;
const MAP_FOCUS_PADDING = 10;
const MAP_MAX_FOCUS_DISTANCE = 1; // km

export default {

  components: {
    Mapbox,
  },
  // Dependency injection provider- see `Marker.vue`
  provide() {
    return {
      getMap: this.getMap,
      addMarker: this.addMarker,
      removeMarker: this.removeMarker,
      getMarker: this.getMarker,
      setPopupItem: this.setPopupItem,
      registerClickedMarkerListener: this.registerClickedMarkerListener,
    };
  },

  props: {
    layers: {
      type: Array,
      required: true
    },
    sources: {
      type: Array,
      required: true
    },
  },

  data() {
    return {
      listener: null,
      center: null,
      map: null,
      loaded: false,
    };
  },

  watch: {
    sources: {
      handler: function () {
        this.calculateFirstCenter();

        for (const data of this.sources) {
          const source = globalMap.getSource(data.id);
          if (!source) {
            this.addSource(data.id, { type: 'geojson', data });
          } else {
            this.setSourceData(data);
          }
        }

        for (const l of this.layers) {
          this.addLayer(l);
        }

        this.focusToAllSources();
      },
    },

    'sources.length': function () { },
  },

  activated() {
    if (globalMap) {
      globalMap.resize();
      this.focusToAllSources();
    }
  },

  created() {
    this.calculateFirstCenter();
  },

  beforeDestroy() {
    for (const key in markers) {
      markers[key].remove();
    }

    globalMap = null;
    markers = {};
    popup = null;
    clickedMarker = null;
  },

  methods: {
    getAllMarkers() {
      return markers;
    },

    setSourceData(fc) {
      if (globalMap) {
        const source = globalMap.getSource(fc.id);
        if (source) {
          source.setData(fc);
          // this.focusToAllSources();
        }
      }
    },

    initPopup() {
      // eslint-disable-next-line no-undef
      popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
      });
      const mountElm = document.createElement('div');
      popup.setDOMContent(mountElm).addTo(globalMap);
      const PortalTargetConstructor = Vue.extend(PortalTarget);
      const target = new PortalTargetConstructor({
        propsData: { name: 'mapbox-popup' },
      });
      target.$mount(mountElm);
    },

    getMap(found) {
      if (globalMap) {
        found(globalMap);
        return;
      }

      const unwatcher = this.$watch('loaded', () => {
        if (this.loaded) {
          found(globalMap);
          unwatcher();
        }
      });
    },

    mapLoaded(map) {
      globalMap = map;
      map.doubleClickZoom.disable();
      map.on('click', (e) => {
        this.$emit('map-click', e);
      });
      map.on('zoom', (e) => {
        this.$emit('zoom', e);
      });
      for (const data of this.sources) {
        this.addSource(data.id, { type: 'geojson', data });
      }
      for (const l of this.layers) {
        this.addLayer(l);
      }
      this.focusToAllSources();
      this.initPopup();

      this.$emit('map-loaded', map);

      this.loaded = true;
    },

    calculateFirstCenter() {
      // if (this.sources.length > 0) {
      //   const merged = geojsonMerge.merge(this.sources);
      //   this.center = centroid(merged).geometry.coordinates;
      // }
    },

    // point - [ lg , lt ]
    focusToPoint(point) {
      globalMap.flyTo({
        center: point,
        essential: true,
        zoom: MAP_FOCUS_ZOOM,
        speed: MAP_FOCUS_SPEED,
      });
    },

    focusToAllSources() {
      if (this.sources.length == 0) {
        return;
      }

      if (!globalMap) {
        return;
      }

      const featureCollections = [];

      for (let source of this.sources) {
        if (globalMap) {
          const mapSource = globalMap.getSource(source.id);
          featureCollections.push(mapSource._data);
        } else {
          return;
        }
      }

      // const mergedFC = geojsonMerge.merge(featureCollections);
      const bbox = TurfBbox(featureCollections[0]);

      if (bbox.includes(Infinity)) {
        return;
      }

      const { coordinates } = featureCollections[0].features[0].geometry;

      const p1 = TurfPoint([bbox[0], bbox[1]]);
      const p2 = TurfPoint([bbox[2], bbox[3]]);

      const distance = TurfDistance(p1, p2, 'kilometers');



      if (coordinates.length === 1 || distance <= MAP_MAX_FOCUS_DISTANCE) {
        globalMap.flyTo({
          center: coordinates[0],
          essential: true,
          zoom: MAP_FOCUS_ZOOM,
          speed: MAP_FOCUS_SPEED,
        });
        return;
      }

      globalMap.fitBounds(bbox, {
        padding: MAP_FOCUS_PADDING,
        speed: MAP_FOCUS_SPEED,
      });
    },

    // TODO addSource & addLayer can be united
    addLayer(layer) {
      if (!globalMap) {
        return;
      }
      if (!globalMap.getLayer(layer.id)) {
        globalMap.addLayer(layer);
      }
    },

    addSource(id, source) {
      if (globalMap) {
        if (!globalMap.getSource(id)) {
          globalMap.addSource(id, source);
        }
      }
    },

    addMarker(key, m) {
      if (markers[key]) {
        markers[key].remove();
        delete markers[key];
        console.warn('Duplicated ids for marker', key);
      }
      markers[key] = m;
      m.addTo(globalMap);
    },

    removeMarker(key) {
      const m = markers[key];
      if (!m) {
        console.warn(`Marker ${key} was not found`);
        return;
      }
      m.remove();
      delete markers[key];
    },

    getMarker(key) {
      return markers[key];
    },

    setPopupItem(marker) {
      clickedMarker = markers[marker.properties.id];
      clickedMarker.setPopup(popup);

      if (!clickedMarker.getPopup().isOpen()) {
        clickedMarker.togglePopup();
      }
      popup.on('close', () => { });
      this.notifyPopup(marker);
    },

    registerClickedMarkerListener(cb) {
      this.listener = cb;
    },

    notifyPopup(payload) {
      this.listener(payload);
    },
  },
};
</script>

<style lang="scss">
.mapboxgl-popup {
  z-index: 2000;
}

.mapboxgl-ctrl-bottom-left {
  display: none;
}

.mapboxgl-ctrl-bottom-right {
  display: none;
}

.mapbox-map-container {
  width: 100%;
  height: 100%;
  position: relative;

  .mapboxgl-map {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
  }
}
</style>